import { Button } from '@assembly-js/design-system';
import { Skeleton } from '@mui/material';
import { Auth } from 'aws-amplify';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WORKSPACES_PAGE } from 'src/constants';
import { PortalConfigContext, RouteContext } from 'src/context';
import history from 'src/history';
import { StudioAuthLogo } from 'src/routes/onboarding/setup/components/studio/StudioAuthLogo';
import { StudioPageFrame } from 'src/routes/onboarding/setup/components/studio/StudioPageFrame';
import { useOnboardingQueryParams } from 'src/routes/onboarding/setup/hooks/useOnboardingQueryParams';
import { buildStudioLoginPath } from 'src/routes/onboarding/setup/utils/buildStudioLoginPath';
import { buildStudioSignupPath } from 'src/routes/onboarding/setup/utils/buildStudioSignupPath';
import {
  EmailToUserIdMap,
  getEmailsFromPortalSessions,
} from 'src/routes/onboarding/setup/utils/getEmailsFromPortalSessions';
import { storeStudioPostLoginResume } from 'src/routes/onboarding/setup/utils/studioPostLoginResume';

export const StudioSelectEmail = () => {
  const { t } = useTranslation();
  const portalConfig = useContext(PortalConfigContext);
  const { sessionData } = useContext(RouteContext);
  const portalIdToSession = sessionData?.portalIdToSession;
  const { prompt, workspaceTemplateId, workspaceTemplateName, templateId } =
    useOnboardingQueryParams();
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [userEmails, setUserEmails] = useState<EmailToUserIdMap>({});

  useEffect(() => {
    try {
      setIsSessionLoading(true);
      setUserEmails(getEmailsFromPortalSessions(portalIdToSession || {}));
    } catch (error) {
      console.error(error);
    } finally {
      setIsSessionLoading(false);
    }
  }, [portalIdToSession]);

  // Display-only templateName / templateDescription are not resumed here.
  const studioSignupParams = {
    prompt,
    workspaceTemplateId,
    workspaceTemplateName,
    templateId,
  };

  const handleSelectEmail = async (
    selectedEmail: string,
    selectedUserId: string,
  ) => {
    // Cognito LastAuthUser must match the selected email's user before
    // refreshing the session (multi-email cookie jars).
    window.localStorage.setItem(
      `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
      selectedUserId,
    );

    try {
      await Auth.currentSession();
      history.push(
        buildStudioSignupPath({
          ...studioSignupParams,
          workspaceSetup: true,
        }),
      );
    } catch (e) {
      console.error('current session not found', e);
      // IU login `next` only reliably carries paths without nested query params.
      // Stash the full Studio resume URL, then land on /workspaces after login
      // which consumes and continues into workspace setup.
      const resumePath = buildStudioSignupPath({
        ...studioSignupParams,
        workspaceSetup: true,
      });
      storeStudioPostLoginResume(resumePath);
      // Keep Studio referrer on the login URL so flag-off re-auth stays on the
      // Studio login screen; /workspaces then consumes the resume path.
      window.location.replace(
        buildStudioLoginPath({
          email: selectedEmail,
          next: WORKSPACES_PAGE,
        }),
      );
    }
  };

  const handleUseDifferentEmail = () => {
    history.push(buildStudioSignupPath(studioSignupParams));
  };

  return (
    <StudioPageFrame>
      <StudioAuthLogo className="mb-3" />
      <h1 className="m-0 text-center font-studio text-2xl font-normal text-black sm:text-[26px]">
        {t(($) => $.page_onboarding.studio.select_email.title)}
      </h1>

      <div
        className="mt-12 flex w-full flex-col gap-3"
        data-testid="existing-users-list"
      >
        <p className="m-0 text-center text-[13px] font-medium text-black/50">
          {t(($) => $.page_onboarding.studio.select_email.continue_with)}
        </p>

        {isSessionLoading && (
          <Skeleton variant="rectangular" className="h-12 w-full rounded-md" />
        )}

        {!isSessionLoading &&
          Object.entries(userEmails).map(([userEmail, userId]) => (
            <Button
              className="h-12 w-full"
              key={userEmail}
              variant="secondary"
              size="sm"
              label={userEmail}
              onClick={() => void handleSelectEmail(userEmail, userId)}
            />
          ))}

        <Button
          className="h-12 w-full"
          variant="secondary"
          size="sm"
          label={t(
            ($) => $.page_onboarding.studio.select_email.use_different_email,
          )}
          onClick={handleUseDifferentEmail}
        />
      </div>
    </StudioPageFrame>
  );
};
